/**
 * Medication scheduling: dose times, countdowns and meal-relative dosing.
 *
 * Ported from patient_dashboard.js (`getNextDoseTime`, `getNextDoseCountdown`,
 * `generateDoseSchedule`, `getNextDoseTimeRemaining`, `isMealTimeMed`,
 * `getMealRemainingSeconds`, `getMealCountdown`, `getNextMealTimeStr`) and the
 * near-identical copies in admin_medicines.js and alarm.js.
 *
 * The legacy versions returned HTML strings. These return data; rendering is the
 * component's job. That separation is what makes the schedule testable.
 */

import { secondsSinceMidnight, formatCountdown, formatSecondsAs12h } from './dates';

const DAY_SECONDS = 86400;

export const MEAL_KEYS = ['breakfast', 'lunch', 'dinner'];

export const MEAL_LABELS = {
  breakfast: 'الإفطار',
  lunch: 'الغداء',
  dinner: 'العشاء',
};

/** Defaults used when a patient has not set their own meal times. */
export const DEFAULT_MEAL_TIMES = {
  breakfast: '08:00',
  lunch: '14:00',
  dinner: '20:00',
};

const toDaySeconds = (time) => {
  if (!time) return Number.NaN;
  const [h, m] = String(time).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return Number.NaN;
  return h * 3600 + m * 60;
};

/**
 * Every dose time for one day, as seconds since midnight.
 *
 * Doses are spaced evenly across 24 hours: `frequency` doses means one every
 * `24 / frequency` hours, starting at `firstDoseTime`. Times that run past
 * midnight wrap to the start of the same day, which is what the legacy schedule
 * badges displayed.
 *
 * Given the patient's `mealTimes`, a "with meals" medicine follows them
 * instead (see `mealDoses`).
 *
 * @param {{firstDoseTime?: string, frequency?: number}} med
 * @param {object|null} [mealTimes]
 * @returns {number[]} Ascending is *not* guaranteed — wrapped doses sort earlier.
 */
export function doseTimesInSeconds(med, mealTimes = null) {
  if (mealTimes && isMealTimeMedicine(med)) return mealDoses(med, mealTimes).map((dose) => dose.seconds);
  const first = toDaySeconds(med?.firstDoseTime);
  const frequency = Number(med?.frequency);
  if (Number.isNaN(first) || !Number.isFinite(frequency) || frequency <= 0) return [];

  const interval = DAY_SECONDS / frequency;
  return Array.from({ length: Math.floor(frequency) }, (_, i) => {
    const raw = first + i * interval;
    return raw >= DAY_SECONDS ? raw - DAY_SECONDS : raw;
  });
}

/**
 * The schedule as render-ready rows. A "with meals" medicine's rows (given
 * `mealTimes`) carry their meal, and the next of them is the next dose.
 *
 * @returns {Array<{seconds: number, time: string, isNext: boolean, isPast: boolean, meal?: string}>}
 */
export function buildDoseSchedule(med, now = new Date(), mealTimes = null) {
  const current = secondsSinceMidnight(now);

  if (mealTimes && isMealTimeMedicine(med)) {
    const doses = mealDoses(med, mealTimes);
    const next = doses.find((dose) => dose.seconds > current);
    return doses.map((dose) => ({
      seconds: dose.seconds,
      time: formatSecondsAs12h(dose.seconds),
      meal: dose.meal,
      isNext: dose === next,
      isPast: dose.seconds < current,
    }));
  }

  const frequency = Number(med?.frequency) || 0;
  const interval = frequency > 0 ? DAY_SECONDS / frequency : DAY_SECONDS;

  return doseTimesInSeconds(med).map((seconds) => ({
    seconds,
    time: formatSecondsAs12h(seconds),
    isNext: seconds > current && seconds - current <= interval,
    isPast: seconds < current,
  }));
}

/**
 * Seconds until the next dose, rolling into tomorrow when today's are done.
 * Returns `null` when the medicine has no usable schedule. Given `mealTimes`,
 * a "with meals" medicine's next dose is its next meal.
 */
export function secondsUntilNextDose(med, now = new Date(), mealTimes = null) {
  const current = secondsSinceMidnight(now);

  if (mealTimes && isMealTimeMedicine(med)) {
    const times = doseTimesInSeconds(med, mealTimes);
    if (times.length === 0) return null;
    const upcoming = times.filter((seconds) => seconds > current);
    const next = upcoming.length ? Math.min(...upcoming) : Math.min(...times) + DAY_SECONDS;
    return Math.round(next - current);
  }

  const first = toDaySeconds(med?.firstDoseTime);
  const frequency = Number(med?.frequency);
  if (Number.isNaN(first) || !Number.isFinite(frequency) || frequency <= 0) return null;

  const interval = DAY_SECONDS / frequency;

  let next = first;
  // Walk forward one interval at a time until we pass "now".
  const guardLimit = Math.ceil(DAY_SECONDS / interval) + 2;
  for (let i = 0; next <= current && i < guardLimit; i += 1) next += interval;

  if (next >= DAY_SECONDS) next -= DAY_SECONDS;

  let remaining = next - current;
  if (remaining < 0) remaining += DAY_SECONDS;
  return Math.round(remaining);
}

/** Next dose as a wall-clock string, e.g. "3:05 PM". */
export function nextDoseTime(med, now = new Date(), mealTimes = null) {
  const remaining = secondsUntilNextDose(med, now, mealTimes);
  if (remaining === null) return '--:--';
  return formatSecondsAs12h(secondsSinceMidnight(now) + remaining);
}

/** Next dose as an "HH:MM:SS" countdown. */
export function nextDoseCountdown(med, now = new Date(), mealTimes = null) {
  const remaining = secondsUntilNextDose(med, now, mealTimes);
  if (remaining === null) return '⏳ حدد موعد الجرعة';
  return formatCountdown(remaining);
}

/** The meal a "with meals" medicine's next dose is taken with, or `null`. */
export function nextDoseMeal(med, now = new Date(), mealTimes = null) {
  if (!mealTimes || !isMealTimeMedicine(med)) return null;
  const doses = mealDoses(med, mealTimes);
  if (doses.length === 0) return null;
  const current = secondsSinceMidnight(now);
  const upcoming = doses.filter((dose) => dose.seconds > current);
  const pool = upcoming.length ? upcoming : doses;
  return pool.reduce((first, dose) => (dose.seconds < first.seconds ? dose : first)).meal;
}

/** Arabic prose form: "الجرعة الجاية بعد ٣ ساعة و ١٠ دقيقة". */
export function nextDoseSummary(med, now = new Date(), mealTimes = null) {
  const remaining = secondsUntilNextDose(med, now, mealTimes);
  if (remaining === null) return 'حدد وقت أول جرعة';

  const totalMinutes = Math.round(remaining / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `الجرعة الجاية بعد ${minutes} دقيقة`;
  if (minutes === 0) return `الجرعة الجاية بعد ${hours} ساعة`;
  return `الجرعة الجاية بعد ${hours} ساعة و ${minutes} دقيقة`;
}

/* ==========================================================================
   Meal-relative dosing
   ========================================================================== */

/** How doctors write each meal ("العشا" and not "عشا", which is in «عشان»). */
const MEAL_WORDS = {
  breakfast: ['فطار', 'فطور'],
  lunch: ['الغدا'],
  dinner: ['العشا'],
};

/** Words that tie a dose to eating without naming the meal. */
const ANY_MEAL_WORDS = ['وجبات', 'وجبة', 'الأكل', 'الاكل', 'الطعام', 'الغذاء'];

/** How far ahead of its meal a "before" dose rings. */
export const BEFORE_MEAL_MINUTES = 30;

/** The doctor's instruction as one text: the dose and its timing. */
const instructionText = (med) => `${med?.dose || ''} ${med?.dosage || ''} ${med?.timing || ''}`;

/** The meals an instruction names, in day order: "بعد الإفطار والعشاء" → breakfast, dinner. */
export function mealsNamed(med) {
  const text = instructionText(med);
  return MEAL_KEYS.filter((key) => MEAL_WORDS[key].some((word) => text.includes(word)));
}

/**
 * True when the doctor tied the dose to meals — "مع الوجبات", "بعد الأكل",
 * "قبل الإفطار", "بعد الإفطار والعشاء" — rather than to a clock. Such
 * medicines follow the patient's meal times instead.
 */
export function isMealTimeMedicine(med) {
  const text = instructionText(med).toLowerCase();
  return ANY_MEAL_WORDS.some((word) => text.includes(word)) || mealsNamed(med).length > 0;
}

/** "قبل" or "بعد" the meal, as the instruction says; "مع" when it says neither or both. */
function mealRelation(med) {
  const text = instructionText(med);
  const before = text.includes('قبل');
  const after = text.includes('بعد');
  if (before && !after) return 'قبل';
  if (after && !before) return 'بعد';
  return 'مع';
}

/** A meal dose as the patient reads it: "بعد العشاء", "قبل الإفطار", "مع الغداء". */
export function doseMealPhrase(med, meal) {
  return meal ? `${mealRelation(med)} ${meal}` : '';
}

/**
 * A prescription's frequency as doses a day: "مرتين يومياً" → 2,
 * "3 مرات يومياً" → 3, "كل 8 ساعات" → 3. `null` when it cannot be read.
 */
export function dosesPerDay(frequency) {
  if (typeof frequency === 'number') return frequency > 0 ? frequency : null;
  const text = String(frequency || '').replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
  const every = text.match(/كل\s*(\d+)\s*ساع/);
  if (every) {
    const hours = Number(every[1]);
    return hours > 0 && hours <= 24 ? Math.round(24 / hours) : null;
  }
  if (/مرتين|مرتان/.test(text)) return 2;
  const times = text.match(/(\d+)\s*مر/);
  if (times) return Number(times[1]) || null;
  if (/ثلاث/.test(text)) return 3;
  if (/أربع|اربع/.test(text)) return 4;
  if (/مرة|مره/.test(text)) return 1;
  return null;
}

/** Meal times sorted by clock position, invalid entries dropped. */
function sortedMeals(mealTimes = {}) {
  return MEAL_KEYS.filter((key) => mealTimes[key])
    .map((key) => ({ key, label: MEAL_LABELS[key], seconds: toDaySeconds(mealTimes[key]) }))
    .filter((meal) => !Number.isNaN(meal.seconds))
    .sort((a, b) => a.seconds - b.seconds);
}

/**
 * A "with meals" medicine's doses: at the patient's meal times. The meals the
 * doctor named when the instruction names them ("بعد الإفطار والعشاء");
 * otherwise as many as it is taken a day — every meal when it is taken as
 * often as there are meals, the first and the last (breakfast and dinner)
 * when twice, and once a day the meal nearest the time the patient set. A
 * dose taken "قبل" its meal rings `BEFORE_MEAL_MINUTES` ahead of it. Earliest
 * first.
 *
 * @returns {Array<{seconds: number, meal: string}>}
 */
export function mealDoses(med, mealTimes) {
  const meals = sortedMeals(mealTimes);
  if (meals.length === 0) return [];

  const named = mealsNamed(med);
  let picked;
  if (named.length > 0) {
    picked = meals.filter((meal) => named.includes(meal.key));
  } else {
    const frequency = Math.floor(Number(med?.frequency));
    const count = Number.isFinite(frequency) && frequency > 0 ? Math.min(frequency, meals.length) : meals.length;
    if (count === meals.length) {
      picked = meals;
    } else if (count === 1) {
      const chosen = toDaySeconds(med?.firstDoseTime);
      const gap = (seconds) => {
        const d = Math.abs(seconds - chosen);
        return Math.min(d, DAY_SECONDS - d);
      };
      picked = [Number.isNaN(chosen) ? meals[0] : meals.reduce((best, meal) => (gap(meal.seconds) < gap(best.seconds) ? meal : best))];
    } else {
      picked = Array.from({ length: count }, (_, i) => meals[Math.round((i * (meals.length - 1)) / (count - 1))]);
    }
  }

  const lead = mealRelation(med) === 'قبل' ? BEFORE_MEAL_MINUTES * 60 : 0;
  return picked
    .map((meal) => ({ seconds: (meal.seconds - lead + DAY_SECONDS) % DAY_SECONDS, meal: meal.label }))
    .sort((a, b) => a.seconds - b.seconds);
}

/** Seconds until the next meal, wrapping to tomorrow's first. `null` if none set. */
export function secondsUntilNextMeal(mealTimes, now = new Date()) {
  const meals = sortedMeals(mealTimes);
  if (meals.length === 0) return null;

  const current = secondsSinceMidnight(now);
  const upcoming = meals.find((meal) => meal.seconds > current);
  const target = upcoming ? upcoming.seconds : meals[0].seconds + DAY_SECONDS;
  return target - current;
}

/** "HH:MM:SS" until the next meal. */
export function mealCountdown(mealTimes, now = new Date()) {
  const remaining = secondsUntilNextMeal(mealTimes, now);
  if (remaining === null) return '--:--:--';
  return formatCountdown(remaining);
}

/** The next meal itself, or `null` when no meal times are configured. */
export function nextMeal(mealTimes, now = new Date()) {
  const meals = sortedMeals(mealTimes);
  if (meals.length === 0) return null;
  const current = secondsSinceMidnight(now);
  return meals.find((meal) => meal.seconds > current) || meals[0];
}

/**
 * e.g. "8:00 AM — الإفطار". The time is wrapped in a left-to-right isolate
 * (U+2066 … U+2069): inside an Arabic sentence it would otherwise read
 * "AM 8:00", its digits and its AM/PM laid out as two right-to-left runs.
 */
export function nextMealLabel(mealTimes, now = new Date()) {
  const meal = nextMeal(mealTimes, now);
  if (!meal) return '--:--';
  return `\u2066${formatSecondsAs12h(meal.seconds)}\u2069 — ${meal.label}`;
}

/* ==========================================================================
   Course tracking
   ========================================================================== */

/**
 * Days left in a course, given a start date and a duration in days.
 * Returns `null` when the medicine has no defined duration (open-ended).
 */
export function courseDaysRemaining(med, now = new Date()) {
  const duration = Number(med?.durationDays ?? med?.duration);
  if (!Number.isFinite(duration) || duration <= 0) return null;

  const startRaw = med.startDate || med.createdAt;
  if (!startRaw) return duration;

  const start = new Date(startRaw);
  if (Number.isNaN(start.getTime())) return duration;

  start.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const elapsed = Math.floor((today - start) / 86400000);
  return Math.max(0, duration - elapsed);
}

/**
 * A prescription's duration as days: "3 شهور" → 90, "أسبوعين" → 14,
 * "10 أيام" → 10. 0 for a medicine taken with no end ("مستمر"), which
 * `courseDaysRemaining` reads as open-ended; `null` when it cannot be read.
 */
export function courseLengthDays(duration) {
  if (typeof duration === 'number') return duration >= 0 ? duration : null;
  const text = String(duration || '').replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
  if (!text.trim()) return null;
  if (/مستمر|دائم|مدى الحياة/.test(text)) return 0;
  const n = Number(text.match(/(\d+)/)?.[1]);
  const count = Number.isFinite(n) && n > 0 ? n : null;
  if (/شهرين/.test(text)) return 60;
  if (/شهر|شهور|أشهر|اشهر/.test(text)) return (count ?? 1) * 30;
  if (/أسبوعين|اسبوعين/.test(text)) return 14;
  if (/أسبوع|اسبوع|أسابيع|اسابيع/.test(text)) return (count ?? 1) * 7;
  if (/يومين/.test(text)) return 2;
  if (/يوم|أيام|ايام/.test(text)) return count;
  return null;
}

/** True when a course has run its full length. */
export function isCourseComplete(med, now = new Date()) {
  const remaining = courseDaysRemaining(med, now);
  return remaining !== null && remaining <= 0;
}

/**
 * Key identifying one alarm firing, so the same dose is never announced twice.
 * Matches the legacy `firedKeys` format ("medId|HH:MM").
 */
export function doseAlarmKey(medId, doseSeconds) {
  const h = String(Math.floor(doseSeconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((doseSeconds % 3600) / 60)).padStart(2, '0');
  return `${medId}|${h}:${m}`;
}

/**
 * Doses that are due right now, within `toleranceSeconds` of their scheduled
 * time and not already fired. This is what drives the alarm. Given
 * `mealTimes`, a "with meals" medicine is due at its meals, and says which.
 *
 * @returns {Array<{med: object, seconds: number, key: string, meal?: string}>}
 */
export function dueDoses(medicines = [], firedKeys = new Set(), now = new Date(), toleranceSeconds = 60, mealTimes = null) {
  const current = secondsSinceMidnight(now);
  const due = [];

  for (const med of medicines) {
    if (!med?.alarmEnabled) continue;
    if (isCourseComplete(med, now)) continue;

    const withMeals = mealTimes && isMealTimeMedicine(med);
    const doses = withMeals ? mealDoses(med, mealTimes) : doseTimesInSeconds(med).map((seconds) => ({ seconds }));
    for (const { seconds, meal } of doses) {
      if (Math.abs(current - seconds) > toleranceSeconds) continue;
      const key = doseAlarmKey(med.id, seconds);
      if (firedKeys.has(key)) continue;
      due.push(meal ? { med, seconds, key, meal } : { med, seconds, key });
    }
  }

  return due;
}

/**
 * The alarm to ring now, if any: a snoozed dose whose snooze is over comes
 * first — a dose is due only for a minute around its time, so a snooze must
 * be remembered to ring again — then a dose falling due that is not snoozed.
 * A snoozed dose of a medicine since deleted or silenced does not ring.
 *
 * @param {object} state
 * @param {Array} state.medicines
 * @param {Set<string>} state.firedKeys  doses already announced today
 * @param {Map<string, {entry: object, until: number}>} state.snoozes  by dose key
 * @param {Date} state.now
 * @param {object|null} [state.mealTimes]
 * @returns {{med: object, seconds: number, key: string, meal?: string, snoozed?: boolean}|null}
 */
export function alarmToRing({ medicines = [], firedKeys = new Set(), snoozes = new Map(), now = new Date(), mealTimes = null }) {
  const at = now.getTime();
  for (const [key, { entry, until }] of snoozes) {
    if (at < until) continue;
    const med = medicines.find((m) => String(m?.id) === String(entry?.med?.id));
    if (!med || !med.alarmEnabled) continue;
    return { ...entry, med, key, snoozed: true };
  }
  return dueDoses(medicines, firedKeys, now, 60, mealTimes).find((dose) => !snoozes.has(dose.key)) || null;
}

/** Where a patient's tracked medicines are kept (this browser only). */
export const medicinesStorageKey = (patientId) => `capsule_patient_medicines_${patientId ?? 'anonymous'}`;

/** Where the meal times the "with meals" doses follow are kept. */
export const MEAL_TIMES_STORAGE_KEY = 'capsule_admin_meal_times';
